// Disponibilidade calculada a partir da config de Test Drive (hosting), SEM o crm.
//
// Espelha a lógica pura do crm-api (`testDriveBooking/availability.ts` + `getAvailableDays`),
// exceto o desconto de vagas — que exige a contagem de bookings ao vivo, irrelevante no
// preview do builder. É usada NO BUILDER: lá o crm público de disponibilidade não é chamado
// (o `process.env` do bundle de federation é vazio), então o wrapper injeta a config e o bloco
// gera dias/horários localmente, igual ao site publicado (menos as vagas).
//
// A lógica está duplicada em crm/hosting/blocks. Se crescer, extrair para um package
// compartilhado `@autodromo/test-drive` — este arquivo é o candidato natural a mover (as
// assinaturas já são as do crm).

export type TdWeekDay = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun"

export interface TdTimeRange {
  start: string
  end: string
}

export type TdWeeklySchedule = Partial<Record<TdWeekDay, TdTimeRange[]>>

export interface TdUnitConfig {
  unitId: string
  enabled: boolean
  scheduleMode: "default" | "custom"
  customSchedule?: TdWeeklySchedule
}

export interface TdSpecialDate {
  date: string
  endDate?: string
  closed: boolean
  schedule?: TdTimeRange[]
  unitIds?: string[] | null
}

export interface TdAvailabilityConfig {
  rules: {
    bookingWindowDays: number
    minLeadTimeDays: number
    slotDurationMinutes: number
  }
  defaultSchedule: TdWeeklySchedule
  units: TdUnitConfig[]
  specialDates: TdSpecialDate[]
}

const WEEKDAY_KEYS: TdWeekDay[] = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
]

const pad = (n: number): string => String(n).padStart(2, "0")

/** YYYY-MM-DD de uma data no fuso local (usado só para derivar "hoje"). */
export const toISODate = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/** Soma dias a um YYYY-MM-DD tratando a data como UTC (aritmética de calendário, sem fuso). */
export const addDaysISO = (iso: string, days: number): string => {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number)
  return h * 60 + m
}

const toHHMM = (minutes: number): string =>
  `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`

/** Divide as faixas do dia pela duração → horários exatos (ex.: 45min → 09:00, 09:45…). */
export const generateSlots = (
  ranges: TdTimeRange[],
  slotDurationMinutes: number,
): string[] => {
  if (!slotDurationMinutes || slotDurationMinutes <= 0) return []
  const slots: string[] = []
  for (const range of ranges) {
    const end = toMinutes(range.end)
    for (
      let cursor = toMinutes(range.start);
      cursor + slotDurationMinutes <= end;
      cursor += slotDurationMinutes
    ) {
      slots.push(toHHMM(cursor))
    }
  }
  return Array.from(new Set(slots)).sort()
}

export const weekdayKey = (dateISO: string): TdWeekDay =>
  WEEKDAY_KEYS[new Date(`${dateISO}T00:00:00Z`).getUTCDay()]

/** Faixas efetivas de uma unidade num dia (ativa/inativa, padrão/próprio, datas especiais). */
export const resolveDayRanges = (
  config: TdAvailabilityConfig,
  unitId: string,
  dateISO: string,
): TdTimeRange[] => {
  const unit = config.units?.find((u) => u.unitId === unitId)
  if (!unit || !unit.enabled) return []

  const special = config.specialDates?.find(
    (s) =>
      s.date <= dateISO &&
      dateISO <= (s.endDate ?? s.date) &&
      (s.unitIds == null || s.unitIds.includes(unitId)),
  )
  if (special) {
    if (special.closed) return []
    if (special.schedule) return special.schedule
  }

  const schedule =
    unit.scheduleMode === "custom" ? unit.customSchedule : config.defaultSchedule
  return schedule?.[weekdayKey(dateISO)] ?? []
}

/** Horários brutos de uma unidade num dia (config, SEM descontar vagas). */
export const slotsForDate = (
  config: TdAvailabilityConfig,
  unitId: string,
  dateISO: string,
): string[] =>
  generateSlots(
    resolveDayRanges(config, unitId, dateISO),
    config.rules?.slotDurationMinutes ?? 0,
  )

/** Janela agendável [hoje+prazo, hoje+prazo+período−1] — espelha o crm getAvailableDays. */
export const availabilityWindow = (
  config: TdAvailabilityConfig,
  todayISO: string,
): { earliest: string; latest: string } => {
  const lead = config.rules?.minLeadTimeDays ?? 0
  const win = config.rules?.bookingWindowDays ?? 3650
  return {
    earliest: addDaysISO(todayISO, lead),
    latest: addDaysISO(todayISO, lead + win - 1),
  }
}

/** Dias abertos de uma unidade em [from, to], dentro da janela agendável. */
export const openDaysInRange = (
  config: TdAvailabilityConfig,
  unitId: string,
  fromISO: string,
  toISO: string,
  todayISO: string,
): string[] => {
  const { earliest, latest } = availabilityWindow(config, todayISO)
  const days: string[] = []
  let cursor = fromISO
  for (let guard = 0; cursor <= toISO && guard < 400; guard++) {
    if (
      cursor >= earliest &&
      cursor <= latest &&
      resolveDayRanges(config, unitId, cursor).length > 0
    ) {
      days.push(cursor)
    }
    cursor = addDaysISO(cursor, 1)
  }
  return days
}

/** Unidades com/sem agenda aberta na janela [amanhã, +lookahead] — espelha useUnitsAvailability. */
export const unitAvailabilitySets = (
  config: TdAvailabilityConfig,
  unitIds: string[],
  todayISO: string,
  lookaheadDays = 60,
): { openUnitIds: Set<string>; closedUnitIds: Set<string> } => {
  const from = addDaysISO(todayISO, 1)
  const to = addDaysISO(from, lookaheadDays)
  const openUnitIds = new Set<string>()
  const closedUnitIds = new Set<string>()
  for (const unitId of unitIds) {
    if (openDaysInRange(config, unitId, from, to, todayISO).length > 0) {
      openUnitIds.add(unitId)
    } else {
      closedUnitIds.add(unitId)
    }
  }
  return { openUnitIds, closedUnitIds }
}
